import { Goal } from 'lucide-react'
import { TeamCrest } from '@/components/TeamCrest'
import { cn } from '@/lib/utils'
import { formatSignPct, isPleno, plenoProbability } from '@/lib/quiniela'
import { GOAL_CATS, type GoalCat, type PlenoView, type QuinielaMatchView } from '@/types/quiniela'

type Side = 'home' | 'away'

function ScoreTile({ value }: { value: GoalCat | null }) {
  return (
    <span
      aria-hidden
      className="figure flex h-14 w-11 items-center justify-center rounded-xl border border-gold/50 bg-black font-display text-[1.9rem] font-black leading-none text-gold shadow-[inset_0_0_12px_rgba(245,158,11,0.10)]"
    >
      {value ?? '–'}
    </span>
  )
}

function GoalRow({
  side, name, logo, marginal, value, onChange, jaPick,
}: {
  side: Side
  name: string
  logo: string | null
  marginal: Record<GoalCat, number> | null
  value: GoalCat | null
  onChange?: (g: GoalCat) => void
  jaPick: GoalCat | null
}) {
  return (
    <div className="grid grid-cols-[minmax(0,6.5rem)_1fr] items-center gap-2.5">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <TeamCrest src={logo} alt={name} size={16} />
          <span className="truncate text-[12px] font-bold text-text">{name}</span>
        </div>
        <span className="text-[9px] text-text-dim pl-[22px]">{side === 'home' ? 'Local' : 'Visitante'}</span>
      </div>
      <div className="grid grid-cols-4 gap-1 rounded-xl border border-gold/20 bg-black/40 p-1">
        {GOAL_CATS.map((g) => {
          const selected = value === g
          const suggested = jaPick === g
          const cls = cn(
            'flex flex-col items-center justify-center rounded-lg h-11 transition-colors',
            selected ? 'bg-gold text-black'
              : suggested ? 'border border-gold/60 text-gold'
              : 'text-text-muted',
          )
          const body = (
            <>
              <span className="figure text-[15px] font-black leading-none">{g}</span>
              <span className={cn('figure text-[9px] mt-1 leading-none', selected ? 'opacity-70' : 'text-text-dim')}>
                {marginal ? formatSignPct(marginal[g]) : '—'}
              </span>
            </>
          )
          return onChange ? (
            <button
              key={g} type="button" aria-pressed={selected}
              aria-label={`${name}: ${g === 'M' ? '3 o más goles' : `${g} goles`}`}
              onClick={() => onChange(g)}
              className={cn(cls, 'active:scale-95')}
            >
              {body}
            </button>
          ) : (
            <div key={g} className={cls}>{body}</div>
          )
        })}
      </div>
    </div>
  )
}

/** Pleno al 15: mecánica propia (goles de cada equipo: 0 / 1 / 2 / M = 3 o más) y por eso identidad visual propia:
 * marcador tipo scoreboard y color dorado (token `gold` existente), distinto de las celdas 1/X/2. Las
 * probabilidades salen de la misma matriz de marcadores de JA. Con `onChange` es seleccionable. */
export function PlenoCard({
  match, pleno, value, onChange, title = 'Pleno al 15', note,
}: {
  match: QuinielaMatchView | undefined
  pleno: PlenoView | { error: string } | null | undefined
  value?: { home: GoalCat; away: GoalCat } | null
  onChange?: (v: { home: GoalCat; away: GoalCat }) => void
  title?: string
  note?: string
}) {
  if (!match) return null
  const ok = isPleno(pleno)
  const shown = value ?? (ok ? { home: pleno.home, away: pleno.away } : null)
  const prob = ok && shown ? plenoProbability(pleno, shown.home, shown.away) : null

  return (
    <section data-testid="pleno-card" className="relative overflow-hidden rounded-2xl border border-gold/35 bg-gradient-to-b from-gold/[0.08] to-panel p-4 space-y-3.5">
      <span className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent" aria-hidden />

      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-gold">
          <Goal className="h-3 w-3" /> {title}
        </span>
        <span className="text-[10px] text-text-dim">Goles de cada equipo</span>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold text-text">{match.home_name} – {match.away_name}</p>
          {prob != null && <p className="text-[11px] text-text-dim mt-0.5">Probabilidad de este marcador <span className="figure font-bold text-gold">{formatSignPct(prob)}</span></p>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0" data-testid="pleno-score-visual">
          <ScoreTile value={shown?.home ?? null} />
          <span className="text-gold/60 font-black" aria-hidden>–</span>
          <ScoreTile value={shown?.away ?? null} />
        </div>
        <span className="sr-only" data-testid="pleno-score">{shown ? `${shown.home} – ${shown.away}` : ''}</span>
      </div>

      {ok || onChange ? (
        <div className="space-y-2.5">
          <GoalRow
            side="home" name={match.home_name} logo={match.home_logo}
            marginal={ok ? pleno.home_marginal : null} value={shown?.home ?? null} jaPick={ok ? pleno.home : null}
            onChange={onChange && ((g) => onChange({ home: g, away: shown?.away ?? (ok ? pleno.away : '1') }))}
          />
          <GoalRow
            side="away" name={match.away_name} logo={match.away_logo}
            marginal={ok ? pleno.away_marginal : null} value={shown?.away ?? null} jaPick={ok ? pleno.away : null}
            onChange={onChange && ((g) => onChange({ home: shown?.home ?? (ok ? pleno.home : '1'), away: g }))}
          />
        </div>
      ) : (
        <p className="text-xs text-warn">JA todavía no tiene el cálculo de goles de este partido.</p>
      )}

      <p className="text-[10px] text-text-dim leading-relaxed">
        {note ?? 'M = 3 o más goles. Cada equipo se marca por separado.'}
        {ok && ' Cálculo con la misma matriz de marcadores de JA.'}
      </p>
    </section>
  )
}
